import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi, templatesApi, clientsApi, reportsApi, participantApi, supervisorApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Users, FileText, ClipboardList, LayoutDashboard, LogOut, FileCheck,
  Plus, Settings, BarChart3, Shield, UserCog, Trash2, Pencil, X, Mail, Link2, Bell, Calendar, MessageSquare, Check, XCircle, Sparkles, Target, TrendingUp, ChevronRight, Clock, DollarSign
} from 'lucide-react';
import { MobileNav } from '../components/MobileNav';
import { MissedShiftsBanner } from '../components/MissedShiftsBanner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { SosBanner } from '../components/SosBanner';
import { InviteOrgDialog } from '../components/InviteOrgDialog';
import { LogoHome } from '../components/LogoHome';
import { LiveShiftsPanel } from '../components/LiveShiftsPanel';
import { TravelCompliancePanel } from '../components/TravelCompliancePanel';
import { MyUpcomingShiftsPanel } from '../components/MyUpcomingShiftsPanel';
import { useConfirm } from '../components/ConfirmProvider';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

export default function AdminDashboard() {
  const confirm = useConfirm();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('worker');
  const [addingUser, setAddingUser] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [deletingClientId, setDeletingClientId] = useState(null);
  // Participant links
  const [participantLinks, setParticipantLinks] = useState([]);
  const [linkUserId, setLinkUserId] = useState('');
  const [linkClientId, setLinkClientId] = useState('');
  const [linkingParticipant, setLinkingParticipant] = useState(false);
  // Notifications & requests
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [shiftRequests, setShiftRequests] = useState([]);
  const [meetingRequests, setMeetingRequests] = useState([]);
  const [showInviteOrg, setShowInviteOrg] = useState(false);
  // Participant Activity (linked portal data)
  const [participantOverview, setParticipantOverview] = useState([]);
  const [participantDetail, setParticipantDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      const [statsRes, usersRes, templatesRes, clientsRes, reportsRes, linksRes, notifsRes, shiftReqRes, meetingReqRes, overviewRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getUsers(),
        templatesApi.getAll(),
        clientsApi.getAll(),
        reportsApi.getAll(),
        participantApi.getLinks().catch(() => ({ data: [] })),
        supervisorApi.getNotifications().catch(() => ({ data: { notifications: [], unread_count: 0 } })),
        supervisorApi.getShiftRequests().catch(() => ({ data: [] })),
        supervisorApi.getMeetingRequests().catch(() => ({ data: [] })),
        participantApi.getAdminOverview().catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setTemplates(templatesRes.data);
      setClients(clientsRes.data);
      setReports(reportsRes.data);
      setParticipantLinks(linksRes.data);
      setNotifications(notifsRes.data.notifications || []);
      setUnreadCount(notifsRes.data.unread_count || 0);
      setShiftRequests(shiftReqRes.data);
      setMeetingRequests(meetingReqRes.data);
      setParticipantOverview(overviewRes.data || []);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedTemplates = async () => {
    try {
      await templatesApi.seedDefaults();
      toast.success('Default templates seeded successfully');
      const templatesRes = await templatesApi.getAll();
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Failed to seed templates');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await adminApi.updateUserRole(userId, newRole);
      toast.success('User role updated');
      const usersRes = await adminApi.getUsers();
      setUsers(usersRes.data);
      const statsRes = await adminApi.getStats();
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const handleAddUser = async () => {
    if (!newUserName || !newUserEmail || !newUserPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    setAddingUser(true);
    try {
      await adminApi.createUser({
        name: newUserName,
        email: newUserEmail,
        password: newUserPassword,
        role: newUserRole
      });
      toast.success(`${newUserName} added as ${newUserRole}`);
      setShowAddUser(false);
      setNewUserName(''); setNewUserEmail(''); setNewUserPassword(''); setNewUserRole('worker');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!(await confirm({ title: `Delete ${userName}?`, description: 'Their account is removed and they can no longer sign in.', confirmText: 'Delete user', destructive: true }))) return;
    setDeletingUserId(userId);
    try {
      await adminApi.deleteUser(userId);
      toast.success(`${userName} has been removed`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      const statsRes = await adminApi.getStats();
      setStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleDeleteClient = async (clientId, clientName) => {
    if (!(await confirm({ title: `Delete ${clientName}?`, description: 'This cannot be undone.', confirmText: 'Delete participant', destructive: true }))) return;
    setDeletingClientId(clientId);
    try {
      await clientsApi.delete(clientId);
      toast.success(`${clientName} removed`);
      setClients(prev => prev.filter(c => c.id !== clientId));
      const statsRes = await adminApi.getStats();
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to delete client');
    } finally {
      setDeletingClientId(null);
    }
  };

  const handleLinkParticipant = async () => {
    if (!linkUserId || !linkClientId) { toast.error('Select both participant and client'); return; }
    setLinkingParticipant(true);
    try {
      const res = await participantApi.linkToClient(linkUserId, linkClientId);
      toast.success(res.data.message);
      setLinkUserId(''); setLinkClientId('');
      loadAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link');
    } finally {
      setLinkingParticipant(false);
    }
  };

  const handleRequestAction = async (type, id, action) => {
    try {
      if (type === 'shift') await supervisorApi.updateShiftRequest(id, action);
      else await supervisorApi.updateMeetingRequest(id, action);
      toast.success(`Request ${action}d`);
      loadAdminData();
    } catch (err) {
      toast.error('Failed to update request');
    }
  };

  const openParticipantDetail = async (userId) => {
    setLoadingDetail(true);
    setParticipantDetail({ loading: true });
    try {
      const res = await participantApi.getAdminParticipantDetail(userId);
      setParticipantDetail(res.data);
    } catch (err) {
      toast.error('Failed to load participant detail');
      setParticipantDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await supervisorApi.markAllRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) { /* silent */ }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  const adminNavItems = [
    { to: '/admin', label: 'Dashboard', icon: <LayoutDashboard className="h-5 w-5" />, active: true },
    { to: '/admin/users', label: 'Users', icon: <Users className="h-5 w-5" /> },
    { to: '/clients', label: 'Clients', icon: <UserCog className="h-5 w-5" /> },
    { to: '/templates', label: 'Templates', icon: <ClipboardList className="h-5 w-5" /> },
    { to: '/reports', label: 'Reports', icon: <FileText className="h-5 w-5" /> },
    { to: '/compliance', label: 'Compliance', icon: <BarChart3 className="h-5 w-5" /> },
    { to: '/supervisor', label: 'Supervisor View', icon: <Shield className="h-5 w-5" /> },
    { to: '/admin/templates', label: 'Template Editor', icon: <Settings className="h-5 w-5" /> },
    { to: '/saved-pdfs', label: 'Saved PDFs', icon: <FileText className="h-5 w-5" /> },
    { to: '/analytics', label: 'Analytics', icon: <BarChart3 className="h-5 w-5 text-teal-400" /> },
    { to: '/team-chat', label: 'Team Chat', icon: <Users className="h-5 w-5" /> },
    { to: '/roster', label: 'Roster', icon: <Clock className="h-5 w-5 text-amber-400" /> },
    { to: '/team-allocations', label: 'Team Allocations', icon: <Users className="h-5 w-5 text-cyan-400" /> },
    { to: '/ndis-export', label: 'NDIS Export', icon: <FileText className="h-5 w-5" /> },
    { to: '/behaviour-tracking', label: 'Behaviour AI', icon: <Shield className="h-5 w-5 text-rose-400" /> },
  ];

  return (
    <div className="min-h-screen bg-slate-900 relative overflow-hidden" data-testid="admin-dashboard">
      {/* Decorative animated gradient blobs */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-72 h-[32rem] w-[32rem] rounded-full bg-teal-500/35 blur-3xl animate-pulse-slow" />
        <div className="absolute top-1/3 right-0 h-[26rem] w-[26rem] rounded-full bg-emerald-500/30 blur-3xl animate-blob-float" />
        <div className="absolute bottom-0 left-1/3 h-96 w-96 rounded-full bg-indigo-500/25 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-[36rem] w-[36rem] rounded-full bg-teal-300/25 blur-3xl animate-pulse-slow" style={{ animationDelay: '3s' }} />
        <div className="absolute top-10 right-1/3 h-80 w-80 rounded-full bg-cyan-400/22 blur-3xl animate-blob-float" style={{ animationDelay: '5s' }} />
        <div className="absolute top-1/2 left-1/2 h-72 w-72 rounded-full bg-amber-300/15 blur-3xl" />
      </div>

      {/* Floating sparkles & geometric accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden hidden lg:block">
        <svg className="absolute top-32 right-32 h-7 w-7 opacity-80 animate-twinkle" viewBox="0 0 24 24" fill="rgb(94 234 212)">
          <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
        </svg>
        <svg className="absolute bottom-1/3 right-12 h-5 w-5 opacity-90 animate-twinkle" style={{ animationDelay: '1.5s' }} viewBox="0 0 24 24" fill="rgb(45 212 191)">
          <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
        </svg>
        <svg className="absolute top-1/2 right-1/4 h-6 w-6 opacity-75 animate-twinkle" style={{ animationDelay: '0.8s' }} viewBox="0 0 24 24" fill="rgb(165 243 252)">
          <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
        </svg>
        <svg className="absolute bottom-32 left-1/2 h-4 w-4 opacity-70 animate-twinkle" style={{ animationDelay: '2.2s' }} viewBox="0 0 24 24" fill="rgb(251 191 36)">
          <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
        </svg>
        <div className="absolute top-[18%] right-[12%] h-3 w-3 rounded-full bg-teal-400/90 animate-float-1" />
        <div className="absolute top-[55%] right-[28%] h-3.5 w-3.5 rounded-full bg-emerald-400/85 animate-float-2" style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-[18%] right-[40%] h-3 w-3 rotate-45 bg-cyan-400/80 animate-float-3" style={{ animationDelay: '1s' }} />
        <div className="absolute top-[35%] right-[8%] h-2.5 w-2.5 rounded-full bg-amber-400/80 animate-float-1" style={{ animationDelay: '3s' }} />
        <div className="absolute top-[70%] left-[60%] h-2 w-12 rounded-full bg-teal-400/40 -rotate-45 animate-float-3" style={{ animationDelay: '4s' }} />
        <svg className="absolute top-1/4 right-1/3 h-28 w-28 opacity-30 animate-spin-slow" viewBox="0 0 100 100" fill="none">
          <polygon points="50,10 85,30 85,70 50,90 15,70 15,30" stroke="rgb(94 234 212)" strokeWidth="1" fill="none" />
          <polygon points="50,25 70,37 70,63 50,75 30,63 30,37" stroke="rgb(45 212 191)" strokeWidth="0.7" fill="rgb(20 184 166 / 0.15)" />
        </svg>
        <svg className="absolute bottom-12 right-1/3 h-32 w-32 opacity-20 animate-spin-slow" style={{ animationDirection: 'reverse' }} viewBox="0 0 200 200" fill="none">
          <circle cx="100" cy="100" r="80" stroke="rgb(45 212 191)" strokeWidth="0.7" />
          <circle cx="100" cy="100" r="60" stroke="rgb(45 212 191)" strokeWidth="0.7" strokeDasharray="2 4" />
          <circle cx="100" cy="100" r="40" stroke="rgb(94 234 212)" strokeWidth="0.7" />
        </svg>
      </div>

      <MobileNav navItems={adminNavItems} user={user} onLogout={handleLogout} />
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-950 border-r border-slate-800 z-50 hidden lg:flex lg:flex-col">
        <div className="p-6 pb-2 flex-shrink-0">
          <LogoHome className="h-16 w-auto mb-4" />
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-2">
          <nav className="space-y-1">
            <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white">
              <LayoutDashboard className="h-5 w-5" />
              Dashboard
            </Link>
            <Link to="/admin/users" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Users className="h-5 w-5" />
              Users
            </Link>
            <Link to="/clients" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <UserCog className="h-5 w-5" />
              Clients
            </Link>
            <Link to="/templates" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <ClipboardList className="h-5 w-5" />
              Templates
            </Link>
            <Link to="/reports" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <FileText className="h-5 w-5" />
              Reports
            </Link>
            <Link to="/compliance" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <BarChart3 className="h-5 w-5" />
              Compliance
            </Link>
            <Link to="/supervisor" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Shield className="h-5 w-5" />
              Supervisor View
            </Link>
            <Link to="/admin/templates" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Settings className="h-5 w-5" />
              Template Editor
            </Link>
            <Link to="/saved-pdfs" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <FileText className="h-5 w-5" />
              Saved PDFs
            </Link>
            <Link to="/analytics" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <BarChart3 className="h-5 w-5 text-teal-400" />
              Analytics
            </Link>
            <Link to="/team-chat" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Users className="h-5 w-5" />
              Team Chat
            </Link>
            <Link to="/roster" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="admin-roster-link">
              <Clock className="h-5 w-5 text-amber-400" />
              Roster
            </Link>
            <Link to="/team-allocations" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="admin-team-allocations-link">
              <Users className="h-5 w-5 text-cyan-400" />
              Team Allocations
            </Link>
            <Link to="/timesheets" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="admin-timesheets-link">
              <Clock className="h-5 w-5 text-amber-400" />
              Timesheets
            </Link>
            <Link to="/payroll" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="admin-payroll-link">
              <DollarSign className="h-5 w-5 text-emerald-400" />
              Payroll
            </Link>
            <Link to="/ndis-export" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <FileText className="h-5 w-5" />
              NDIS Export
            </Link>
            <Link to="/behaviour-tracking" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Shield className="h-5 w-5 text-rose-400" />
              Behaviour AI
            </Link>
          </nav>
        </div>
        <div className="flex-shrink-0 p-6 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-medium">
              {user?.name?.charAt(0)}
            </div>
            <div>
              <p className="text-white font-medium text-sm">{user?.name}</p>
              <p className="text-slate-500 text-xs">Organisation Admin</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64 relative z-10">
        {/* Mobile Header */}
        <header className="lg:hidden bg-slate-950 border-b border-slate-800 px-4 py-4 flex items-center justify-between">
          <LogoHome className="h-12 w-auto" />
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400">
            <LogOut className="h-5 w-5" />
          </Button>
        </header>

        <div className="p-6 lg:p-8">
          {/* SOS Active Banner */}
          <SosBanner />
          {/* Missed shifts alert (last 24h) */}
          <MissedShiftsBanner />
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-2">
              Organisation Dashboard
            </h1>
            <p className="text-slate-400">Manage your organisation's NDIS documentation system</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 border-slate-700 text-slate-300 hover:bg-slate-800"
              onClick={async () => {
                try {
                  const res = await adminApi.sendDigest();
                  toast.success(res.data.message);
                } catch (err) {
                  toast.error('Failed to send digest');
                }
              }}
              data-testid="send-digest-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              Send Weekly Digest
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 ml-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={async () => {
                try {
                  const res = await adminApi.seedDefaults();
                  toast.success(res.data?.message || 'Default templates seeded');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to seed templates');
                }
              }}
              data-testid="seed-templates-btn"
            >
              <FileCheck className="h-4 w-4 mr-2" />
              Seed Default Templates
            </Button>
            <Button
              size="sm"
              className="mt-3 ml-2 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
              onClick={() => setShowInviteOrg(true)}
              data-testid="invite-org-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Invite an Organisation
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 ml-2 border-slate-700 text-slate-300 hover:bg-slate-800"
              onClick={() => navigate('/settings/organisation')}
              data-testid="org-settings-btn"
            >
              <Settings className="h-4 w-4 mr-2" />
              Workspace Settings
            </Button>
          </div>

          <InviteOrgDialog open={showInviteOrg} onOpenChange={setShowInviteOrg} />

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Total Users</p>
                    <p className="text-2xl font-bold text-white">{stats?.total_users || 0}</p>
                  </div>
                  <Users className="h-8 w-8 text-teal-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Workers / Supervisors</p>
                    <p className="text-2xl font-bold text-white">{stats?.total_workers || 0} / {stats?.total_supervisors || 0}</p>
                  </div>
                  <UserCog className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Total Reports</p>
                    <p className="text-2xl font-bold text-white">{stats?.total_reports || 0}</p>
                    <p className="text-xs text-slate-500 mt-1">{stats?.submitted_reports || 0} submitted</p>
                  </div>
                  <FileText className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Clients</p>
                    <p className="text-2xl font-bold text-white">{stats?.total_clients || 0}</p>
                  </div>
                  <ClipboardList className="h-8 w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Live Shifts Panel — workers currently clocked in + live KMs */}
          <LiveShiftsPanel />

          {/* My upcoming shifts (own shifts) */}
          <MyUpcomingShiftsPanel userId={user?.id} />

          {/* Travel-Tracking Compliance Leaderboard */}
          <TravelCompliancePanel />

          {/* Tabs Section */}
          <Tabs defaultValue="users" className="space-y-6">
            <TabsList className="bg-slate-800 border-slate-700 flex-wrap">
              <TabsTrigger value="users" className="data-[state=active]:bg-teal-600">Users</TabsTrigger>
              <TabsTrigger value="templates" className="data-[state=active]:bg-teal-600">Templates</TabsTrigger>
              <TabsTrigger value="clients" className="data-[state=active]:bg-teal-600">Clients</TabsTrigger>
              <TabsTrigger value="reports" className="data-[state=active]:bg-teal-600">Reports</TabsTrigger>
              <TabsTrigger value="participants" className="data-[state=active]:bg-teal-600 relative">
                Participants
              </TabsTrigger>
              <TabsTrigger value="notifications" className="data-[state=active]:bg-teal-600 relative">
                Notifications
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-[16px] bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center px-1">{unreadCount}</span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Users Tab */}
            <TabsContent value="users">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-white">User Management</CardTitle>
                    <CardDescription className="text-slate-400">Manage support workers, supervisors, and admins</CardDescription>
                  </div>
                  <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => setShowAddUser(!showAddUser)} data-testid="add-user-btn">
                    {showAddUser ? <X className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                    {showAddUser ? 'Cancel' : 'Add User'}
                  </Button>
                </CardHeader>
                <CardContent>
                  {showAddUser && (
                    <div className="mb-6 p-4 bg-slate-900 rounded-lg border border-slate-700" data-testid="add-user-form">
                      <h4 className="text-white font-medium mb-4">Add New User</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                        <Input placeholder="Full Name" value={newUserName} onChange={(e) => setNewUserName(e.target.value)} className="bg-slate-800 border-slate-600 text-white" data-testid="new-user-name" />
                        <Input type="email" placeholder="Email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} className="bg-slate-800 border-slate-600 text-white" data-testid="new-user-email" />
                        <Input type="password" placeholder="Password" value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} className="bg-slate-800 border-slate-600 text-white" data-testid="new-user-password" />
                        <Select value={newUserRole} onValueChange={setNewUserRole}>
                          <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="worker">Support Worker</SelectItem>
                            <SelectItem value="supervisor">Supervisor</SelectItem>
                            <SelectItem value="organisation">Organisation</SelectItem>
                            <SelectItem value="participant">Participant / Family</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={handleAddUser} disabled={addingUser} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-add-user">
                        {addingUser ? 'Adding...' : 'Add User'}
                      </Button>
                    </div>
                  )}
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Name</TableHead>
                        <TableHead className="text-slate-400">Email</TableHead>
                        <TableHead className="text-slate-400">Role</TableHead>
                        <TableHead className="text-slate-400">Last clocked in</TableHead>
                        <TableHead className="text-slate-400">Change Role</TableHead>
                        <TableHead className="text-slate-400 text-right">Delete</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.id} className="border-slate-700">
                          <TableCell className="text-white font-medium">{u.name}</TableCell>
                          <TableCell className="text-slate-300 font-mono text-sm">{u.email}</TableCell>
                          <TableCell>
                            <Badge className={u.role === 'organisation' ? 'bg-teal-600' : u.role === 'supervisor' ? 'bg-indigo-600' : 'bg-slate-600'}>
                              {u.role}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-300 text-sm" data-testid={`user-last-clockin-${u.id}`}>
                            {u.last_clock_in_at ? (
                              <span title={new Date(u.last_clock_in_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })}>
                                {(() => {
                                  const diffMs = Date.now() - new Date(u.last_clock_in_at).getTime();
                                  const days = Math.floor(diffMs / 86400000);
                                  if (days === 0) return 'Today';
                                  if (days === 1) return 'Yesterday';
                                  if (days < 7) return `${days}d ago`;
                                  return new Date(u.last_clock_in_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', year: 'numeric' });
                                })()}
                              </span>
                            ) : (
                              <span className="text-slate-500 italic">never</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {u.id !== user?.id && (
                              <div className="flex gap-1">
                                {u.role !== 'worker' && <Button variant="ghost" size="sm" onClick={() => updateUserRole(u.id, 'worker')} className="text-slate-400 hover:text-white text-xs">Worker</Button>}
                                {u.role !== 'supervisor' && <Button variant="ghost" size="sm" onClick={() => updateUserRole(u.id, 'supervisor')} className="text-slate-400 hover:text-white text-xs">Supervisor</Button>}
                                {u.role !== 'organisation' && <Button variant="ghost" size="sm" onClick={() => updateUserRole(u.id, 'organisation')} className="text-slate-400 hover:text-white text-xs">Organisation</Button>}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {u.id !== user?.id && (
                              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-400" onClick={() => handleDeleteUser(u.id, u.name)} disabled={deletingUserId === u.id} data-testid={`delete-user-${u.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {users.length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      <Users className="h-12 w-12 mx-auto mb-3 text-slate-600" />
                      <p>No users yet. Click "Add User" to get started.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Templates Tab */}
            <TabsContent value="templates">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Report Templates</CardTitle>
                    <CardDescription className="text-slate-400">NDIS-aligned documentation templates</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSeedTemplates} variant="outline" className="border-slate-600 text-slate-300" data-testid="seed-templates-btn">
                      <Settings className="h-4 w-4 mr-2" />
                      Seed Defaults
                    </Button>
                    <Link to="/templates/new">
                      <Button className="bg-teal-600 hover:bg-teal-700">
                        <Plus className="h-4 w-4 mr-2" />
                        New Template
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    {templates.map((template) => (
                      <div key={template.id} className="p-4 rounded-lg border border-slate-700 hover:border-teal-500/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-white font-medium">{template.name}</h3>
                            <p className="text-sm text-slate-400 capitalize">{template.type.replace(/_/g, ' ')}</p>
                          </div>
                          <Badge variant="outline" className="border-slate-600 text-slate-300">
                            {template.fields?.length || 0} fields
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {templates.length === 0 && (
                      <div className="text-center py-8 text-slate-400">
                        <ClipboardList className="h-12 w-12 mx-auto mb-3 text-slate-600" />
                        <p>No templates yet. Click "Seed Defaults" to add NDIS templates.</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Clients Tab */}
            <TabsContent value="clients">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Client Management</CardTitle>
                    <CardDescription className="text-slate-400">{clients.length} NDIS participant{clients.length !== 1 ? 's' : ''}</CardDescription>
                  </div>
                  <Link to="/clients/new">
                    <Button className="bg-teal-600 hover:bg-teal-700" data-testid="new-client-btn">
                      <Plus className="h-4 w-4 mr-2" /> New Client
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Name</TableHead>
                        <TableHead className="text-slate-400">NDIS Number</TableHead>
                        <TableHead className="text-slate-400">Cultural Background</TableHead>
                        <TableHead className="text-slate-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clients.map((client) => (
                        <TableRow key={client.id} className="border-slate-700">
                          <TableCell className="text-white font-medium">{client.name}</TableCell>
                          <TableCell className="text-slate-300 font-mono text-sm">{client.ndis_number}</TableCell>
                          <TableCell className="text-slate-300">{client.cultural_background || '-'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Link to={`/clients/${client.id}`}>
                                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </Link>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-slate-400 hover:text-red-400"
                                onClick={() => handleDeleteClient(client.id, client.name)}
                                disabled={deletingClientId === client.id}
                                data-testid={`delete-client-admin-${client.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {clients.length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      <UserCog className="h-12 w-12 mx-auto mb-3 text-slate-600" />
                      <p>No clients yet. Add your first client.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">All Reports</CardTitle>
                  <CardDescription className="text-slate-400">Documentation from all support workers</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Type</TableHead>
                        <TableHead className="text-slate-400">Date</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                        <TableHead className="text-slate-400">Compliance</TableHead>
                        <TableHead className="text-slate-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reports.slice(0, 10).map((report) => (
                        <TableRow key={report.id} className="border-slate-700">
                          <TableCell className="text-white font-medium capitalize">
                            {report.template_type.replace(/_/g, ' ')}
                          </TableCell>
                          <TableCell className="text-slate-300">
                            {new Date(report.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}
                          </TableCell>
                          <TableCell>
                            <Badge variant={report.status === 'submitted' ? 'default' : 'secondary'}
                              className={report.status === 'submitted' ? 'bg-green-600' : 'bg-slate-600'}>
                              {report.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-300">
                            {report.compliance_score ? `${report.compliance_score}%` : '-'}
                          </TableCell>
                          <TableCell>
                            <Link to={`/reports/${report.id}`}>
                              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                                View
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {reports.length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      <FileText className="h-12 w-12 mx-auto mb-3 text-slate-600" />
                      <p>No reports yet.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Participants Tab */}
            <TabsContent value="participants">
              <div className="space-y-6">
                {/* Link Participant Form */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2"><Link2 className="h-5 w-5 text-teal-400" /> Link Participant to Client</CardTitle>
                    <CardDescription className="text-slate-400">Connect a participant/family account to a client profile so they can view reports and track progress.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Select value={linkUserId} onValueChange={setLinkUserId}>
                        <SelectTrigger className="bg-slate-900 border-slate-600 text-white flex-1" data-testid="link-participant-select">
                          <SelectValue placeholder="Select Participant..." />
                        </SelectTrigger>
                        <SelectContent>
                          {users.filter(u => u.role === 'participant').map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={linkClientId} onValueChange={setLinkClientId}>
                        <SelectTrigger className="bg-slate-900 border-slate-600 text-white flex-1" data-testid="link-client-select">
                          <SelectValue placeholder="Select Client..." />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name} (NDIS: {c.ndis_number})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button onClick={handleLinkParticipant} disabled={linkingParticipant} className="bg-teal-600 hover:bg-teal-700" data-testid="link-participant-btn">
                        <Link2 className="h-4 w-4 mr-2" /> {linkingParticipant ? 'Linking...' : 'Link'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Participant Activity Overview (portal integration) */}
                <Card className="bg-slate-800 border-slate-700" data-testid="participant-activity-card">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2"><Sparkles className="h-5 w-5 text-emerald-400" /> Participant Activity Overview</CardTitle>
                    <CardDescription className="text-slate-400">Live portal activity: goals, progress scores, celebrations and pending requests for each linked participant.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {participantOverview.length === 0 ? (
                      <p className="text-slate-500 text-center py-6">No participant activity yet. Link a participant above to get started.</p>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {participantOverview.map(p => (
                          <div
                            key={p.user_id}
                            className="bg-slate-900 border border-slate-700 hover:border-teal-500/50 rounded-lg p-4 cursor-pointer transition-colors"
                            onClick={() => openParticipantDetail(p.user_id)}
                            data-testid={`participant-overview-${p.user_id}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-white font-semibold">{p.user_name}</p>
                                  <Badge variant="outline" className="text-teal-400 border-teal-500/50 text-[10px]">{p.client_name}</Badge>
                                </div>
                                <p className="text-xs text-slate-500">{p.user_email}</p>
                                {p.last_activity && (
                                  <p className="text-[10px] text-slate-600 mt-1">Last active: {new Date(p.last_activity).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}</p>
                                )}
                              </div>
                              <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />
                            </div>
                            <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-slate-700">
                              <div>
                                <div className="flex items-center gap-1 text-[10px] text-slate-500"><Target className="h-3 w-3" /> Goals</div>
                                <p className="text-white text-sm font-semibold mt-0.5">
                                  {p.stats.active_goals}/{p.stats.total_goals}
                                  <span className="text-emerald-400 text-[10px] ml-1">+{p.stats.completed_goals} done</span>
                                </p>
                              </div>
                              <div>
                                <div className="flex items-center gap-1 text-[10px] text-slate-500"><Sparkles className="h-3 w-3" /> Celebrations</div>
                                <p className="text-emerald-400 text-sm font-semibold mt-0.5">{p.stats.celebrations_sent}</p>
                              </div>
                              <div>
                                <div className="flex items-center gap-1 text-[10px] text-slate-500"><TrendingUp className="h-3 w-3" /> Behaviours</div>
                                <p className="text-slate-200 text-sm font-semibold mt-0.5">{p.stats.behaviour_observations}</p>
                              </div>
                              <div>
                                <div className="flex items-center gap-1 text-[10px] text-slate-500"><Bell className="h-3 w-3" /> Pending</div>
                                <p className="text-amber-400 text-sm font-semibold mt-0.5">
                                  {p.stats.pending_shift_requests + p.stats.pending_meeting_requests}
                                </p>
                              </div>
                            </div>
                            {/* Progress scores */}
                            {Object.keys(p.latest_progress || {}).length > 0 && (
                              <div className="flex items-center gap-2 mt-3 flex-wrap">
                                {Object.entries(p.latest_progress).map(([cat, info]) => {
                                  const label = cat.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()).slice(0, 10);
                                  return (
                                    <div key={cat} className="flex items-center gap-1 bg-slate-800 rounded px-2 py-1 border border-slate-700">
                                      <span className="text-[9px] text-slate-500">{label}</span>
                                      <span className="text-xs text-white font-semibold">{info.score}/10</span>
                                      {info.source === 'ai' && <span className="text-[8px] bg-purple-900/40 text-purple-300 px-1 rounded">AI</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Existing Links */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Linked Accounts ({participantLinks.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {participantLinks.length === 0 ? (
                      <p className="text-slate-500 text-center py-6">No participant accounts linked yet</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow className="border-slate-700 hover:bg-transparent">
                            <TableHead className="text-slate-400">Participant</TableHead>
                            <TableHead className="text-slate-400">Email</TableHead>
                            <TableHead className="text-slate-400">Linked Client</TableHead>
                            <TableHead className="text-slate-400">Linked</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {participantLinks.map((link, i) => (
                            <TableRow key={i} className="border-slate-700" data-testid="participant-link-row">
                              <TableCell className="text-white font-medium">{link.user_name}</TableCell>
                              <TableCell className="text-slate-300">{link.user_email}</TableCell>
                              <TableCell className="text-teal-400">{link.client_name}</TableCell>
                              <TableCell className="text-slate-500 text-xs">{link.linked_at ? new Date(link.linked_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' }) : ''}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>

                {/* Shift Requests */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader><CardTitle className="text-white flex items-center gap-2"><Calendar className="h-5 w-5 text-amber-400" /> Shift Requests</CardTitle></CardHeader>
                  <CardContent>
                    {shiftRequests.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No shift requests</p>
                    ) : (
                      <div className="space-y-2">
                        {shiftRequests.map(r => (
                          <div key={r.id} className="flex items-center justify-between p-3 bg-slate-900 rounded-lg" data-testid="shift-request-admin">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-white font-medium text-sm">{r.user_name}</span>
                                <Badge variant="outline" className={r.request_type === 'cancel' ? 'text-red-400 border-red-500/50' : 'text-teal-400 border-teal-500/50'}>
                                  {r.request_type === 'cancel' ? 'Cancellation' : 'New Shift'}
                                </Badge>
                                <span className="text-slate-500 text-xs">for {r.client_name}</span>
                              </div>
                              <p className="text-xs text-slate-400 mt-0.5">{r.preferred_date}{r.preferred_time ? ` at ${r.preferred_time}` : ''}{r.notes ? ` - ${r.notes}` : ''}</p>
                            </div>
                            {r.status === 'pending' ? (
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost" className="text-emerald-400 hover:bg-emerald-600/20 h-8" onClick={() => handleRequestAction('shift', r.id, 'approve')}>
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-600/20 h-8" onClick={() => handleRequestAction('shift', r.id, 'decline')}>
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <Badge variant="outline" className={r.status === 'approved' ? 'text-emerald-400 border-emerald-500/50' : 'text-red-400 border-red-500/50'}>{r.status}</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Meeting Requests */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader><CardTitle className="text-white flex items-center gap-2"><MessageSquare className="h-5 w-5 text-blue-400" /> Meeting Requests</CardTitle></CardHeader>
                  <CardContent>
                    {meetingRequests.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No meeting requests</p>
                    ) : (
                      <div className="space-y-2">
                        {meetingRequests.map(r => (
                          <div key={r.id} className="flex items-center justify-between p-3 bg-slate-900 rounded-lg" data-testid="meeting-request-admin">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-white font-medium text-sm">{r.user_name}</span>
                                <Badge variant="outline" className="text-blue-400 border-blue-500/50">Meeting with {r.requested_with}</Badge>
                                <span className="text-slate-500 text-xs">for {r.client_name}</span>
                              </div>
                              <p className="text-xs text-slate-400 mt-0.5">{r.preferred_date ? `Preferred: ${r.preferred_date}` : ''}{r.reason ? ` - ${r.reason}` : ''}</p>
                            </div>
                            {r.status === 'pending' ? (
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost" className="text-emerald-400 hover:bg-emerald-600/20 h-8" onClick={() => handleRequestAction('meeting', r.id, 'approve')}>
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-600/20 h-8" onClick={() => handleRequestAction('meeting', r.id, 'decline')}>
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <Badge variant="outline" className={r.status === 'approved' ? 'text-emerald-400 border-emerald-500/50' : 'text-red-400 border-red-500/50'}>{r.status}</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2"><Bell className="h-5 w-5 text-amber-400" /> Notifications</CardTitle>
                    {unreadCount > 0 && (
                      <Button variant="outline" size="sm" className="border-slate-600 text-slate-300" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
                        Mark all read
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {notifications.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No notifications yet. You'll see shift cancellations and meeting requests here.</p>
                  ) : (
                    <div className="space-y-2">
                      {notifications.map(n => (
                        <div key={n.id} className={`p-3 rounded-lg border transition-colors ${n.read ? 'bg-slate-900 border-slate-800' : 'bg-slate-900 border-teal-600/30'}`} data-testid="notification-item">
                          <div className="flex items-start gap-3">
                            <div className={`p-1.5 rounded-lg mt-0.5 ${n.read ? 'bg-slate-800' : 'bg-teal-600/20'}`}>
                              {n.link_type === 'shift_request' ? <Calendar className="h-3.5 w-3.5 text-amber-400" /> : <MessageSquare className="h-3.5 w-3.5 text-blue-400" />}
                            </div>
                            <div className="flex-1">
                              <p className={`text-sm font-medium ${n.read ? 'text-slate-400' : 'text-white'}`}>{n.title}</p>
                              <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                              <p className="text-[10px] text-slate-600 mt-1">{new Date(n.created_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })}</p>
                            </div>
                            {!n.read && <div className="h-2 w-2 rounded-full bg-teal-400 mt-2 flex-shrink-0"></div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </div>
      </main>

      {/* Participant Detail Dialog */}
      <Dialog open={!!participantDetail} onOpenChange={(o) => { if (!o) setParticipantDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-slate-900 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <UserCog className="h-5 w-5 text-teal-400" />
              {participantDetail?.user?.name || 'Participant'} - Activity Detail
            </DialogTitle>
          </DialogHeader>
          {loadingDetail || participantDetail?.loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400"></div>
            </div>
          ) : participantDetail && participantDetail.user ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg border border-slate-700">
                <div className="flex-1">
                  <p className="text-sm font-medium">{participantDetail.user.name}</p>
                  <p className="text-xs text-slate-400">{participantDetail.user.email}</p>
                </div>
                <Badge variant="outline" className="text-teal-400 border-teal-500/50">Linked to {participantDetail.client?.name}</Badge>
              </div>

              {/* Goals */}
              <div>
                <h4 className="text-sm font-semibold text-slate-300 flex items-center gap-2 mb-2">
                  <Target className="h-4 w-4 text-teal-400" /> Goals ({participantDetail.goals?.length || 0})
                </h4>
                {participantDetail.goals?.length === 0 ? (
                  <p className="text-xs text-slate-500 pl-6">No goals set yet.</p>
                ) : (
                  <div className="space-y-1.5 pl-6">
                    {participantDetail.goals.map(g => (
                      <div key={g.id} className="flex items-center justify-between bg-slate-800 rounded px-3 py-2 border border-slate-700 text-sm" data-testid={`admin-goal-${g.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-200">{g.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <div className="flex-1 h-1 max-w-[120px] bg-slate-700 rounded">
                              <div className="h-full bg-teal-500 rounded" style={{ width: `${g.progress || 0}%` }} />
                            </div>
                            <span className="text-[10px] text-slate-500">{g.progress || 0}%</span>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-[10px] capitalize ml-2 border-slate-600 text-slate-400">{g.status?.replace('_', ' ')}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Celebrations */}
              <div>
                <h4 className="text-sm font-semibold text-slate-300 flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-emerald-400" /> Celebrations Sent ({participantDetail.celebrations?.length || 0})
                </h4>
                {participantDetail.celebrations?.length === 0 ? (
                  <p className="text-xs text-slate-500 pl-6">None yet.</p>
                ) : (
                  <div className="space-y-1.5 pl-6">
                    {participantDetail.celebrations.map(c => (
                      <div key={c.id} className="bg-emerald-900/20 border border-emerald-800/40 rounded px-3 py-2 text-sm" data-testid={`admin-celebration-${c.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-emerald-300 font-medium text-xs">{c.category_name}</p>
                          <span className="text-[10px] text-slate-500">{new Date(c.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}</span>
                        </div>
                        <p className="text-slate-300 text-xs mt-1">{c.message}</p>
                        <p className="text-[10px] text-slate-500 mt-1">Sent to {c.worker_ids?.length || 0} worker(s)</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Progress */}
              <div>
                <h4 className="text-sm font-semibold text-slate-300 flex items-center gap-2 mb-2">
                  <TrendingUp className="h-4 w-4 text-blue-400" /> Recent Progress ({participantDetail.progress?.length || 0})
                </h4>
                {participantDetail.progress?.length === 0 ? (
                  <p className="text-xs text-slate-500 pl-6">No progress logged.</p>
                ) : (
                  <div className="space-y-1 pl-6">
                    {participantDetail.progress.slice(0, 8).map(p => (
                      <div key={p.id} className="flex items-center justify-between text-xs bg-slate-800 rounded px-3 py-1.5 border border-slate-700">
                        <span className="text-slate-300 capitalize">{p.category?.replace('_', ' ')}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-semibold">{p.score}/10</span>
                          {p.source === 'ai' && <Badge className="bg-purple-900/40 text-purple-300 text-[9px] px-1">AI</Badge>}
                          <span className="text-slate-500 text-[10px]">{p.created_at?.slice(0, 10)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Requests */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-slate-300 flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4 text-amber-400" /> Shift Requests ({participantDetail.shift_requests?.length || 0})
                  </h4>
                  {participantDetail.shift_requests?.length === 0 ? (
                    <p className="text-xs text-slate-500 pl-6">None.</p>
                  ) : (
                    <div className="space-y-1 pl-6">
                      {participantDetail.shift_requests.slice(0, 5).map(r => (
                        <div key={r.id} className="bg-slate-800 rounded px-2 py-1.5 border border-slate-700 text-xs flex justify-between">
                          <span className="text-slate-300">{r.request_type === 'cancel' ? 'Cancel' : 'New'} · {r.preferred_date}</span>
                          <Badge variant="outline" className="text-[9px] capitalize border-slate-600">{r.status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-300 flex items-center gap-2 mb-2">
                    <MessageSquare className="h-4 w-4 text-blue-400" /> Meeting Requests ({participantDetail.meeting_requests?.length || 0})
                  </h4>
                  {participantDetail.meeting_requests?.length === 0 ? (
                    <p className="text-xs text-slate-500 pl-6">None.</p>
                  ) : (
                    <div className="space-y-1 pl-6">
                      {participantDetail.meeting_requests.slice(0, 5).map(r => (
                        <div key={r.id} className="bg-slate-800 rounded px-2 py-1.5 border border-slate-700 text-xs flex justify-between">
                          <span className="text-slate-300">With {r.requested_with} · {r.preferred_date || 'any date'}</span>
                          <Badge variant="outline" className="text-[9px] capitalize border-slate-600">{r.status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
